export type Session = {
  access_token: string;
  expires_at: number;
  user: { id: string; email: string };
  aal: "aal1" | "aal2";
};
type Listener = (event: "SIGNED_IN" | "SIGNED_OUT" | "TOKEN_REFRESHED", session: Session | null) => void;
export class AuthError extends Error {
  code: string;
  status: number;
  trace_id?: string;
  constructor(message: string, code: string, status: number, traceId?: string) {
    super(message);
    this.code = code;
    this.status = status;
    this.trace_id = traceId;
  }
}

/** Access credentials live only in this instance. The server owns the refresh cookie. */
export class ProductAuth {
  private session: Session | null = null;
  private initialized = false;
  private generation = 0;
  private refresh: Promise<Session | null> | null = null;
  private listeners = new Set<Listener>();
  private operations: Promise<unknown> = Promise.resolve();
  private channel: BroadcastChannel | null = null;
  private browser = typeof window !== "undefined";
  private locks = this.browser ? navigator.locks : undefined;
  private notifyTabs(kind: "signed-out" | "account-changed") {
    // Never transmit credentials or business data to other browsing contexts.
    this.channel?.postMessage({ kind });
  }
  private enqueue<T>(operation: () => Promise<T>): Promise<T> {
    const pending = this.operations.then(async () => {
      if (this.locks) return await this.locks.request("oqc-auth-cookie", operation);
      return await operation();
    });
    this.operations = pending.catch(() => {});
    return pending;
  }
  private fetcher: typeof fetch;
  constructor(fetcher: typeof fetch = (...args) => globalThis.fetch(...args)) {
    this.fetcher = fetcher;
    if (this.browser && typeof BroadcastChannel !== "undefined") {
      this.channel = new BroadcastChannel("oqc-auth-state");
      this.channel.onmessage = ({ data }) => {
        if (data?.kind !== "signed-out" && data?.kind !== "account-changed") return;
        ++this.generation;
        this.setSession(null, "SIGNED_OUT");
      };
    }
  }
  private setSession(session: Session | null, event: Parameters<Listener>[0]) {
    this.session = session;
    this.initialized = true;
    for (const listener of this.listeners) listener(event, session);
    return session;
  }
  onAuthStateChange(listener: Listener) {
    this.listeners.add(listener);
    return { unsubscribe: () => this.listeners.delete(listener) };
  }
  private async request<T>(path: string, body?: unknown, protectedRequest = false, method = "POST", identity?: Session | null): Promise<T> {
    const session = identity ?? (protectedRequest ? await this.getSession() : null);
    if (protectedRequest && !session) throw new AuthError("请重新登录", "unauthorized", 401);
    const response = await this.fetcher(`/api/v1/auth${path}`, {
      method,
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const payload = response.status === 204 ? null : await response.json().catch(() => null);
    if (!response.ok) {
      const detail = payload?.error || payload?.detail || payload;
      throw new AuthError(typeof detail === "string" ? detail : detail?.message || "请求未完成，请稍后重试", detail?.code || "request_failed", response.status, detail?.trace_id || response.headers.get("X-Request-ID") || undefined);
    }
    return payload as T;
  }
  async getSession(): Promise<Session | null> {
    if (this.session && this.session.expires_at > Date.now() / 1000 + 30) return this.session;
    if (this.initialized && !this.session) return null;
    return this.refreshSession();
  }
  refreshSession(): Promise<Session | null> {
    // Without an origin-wide lock, concurrent tabs can replay a rotated cookie and
    // revoke the legitimate session. Older browsers must log in again instead.
    if (this.browser && !this.locks) return Promise.reject(new AuthError(
      "当前浏览器不支持安全的会话续期，请使用新版浏览器或重新登录", "browser_session_unsupported", 400));
    if (this.refresh) return this.refresh;
    const generation = this.generation;
    const pending = this.enqueue(() => this.request<Session>("/refresh"))
      .then(session => generation === this.generation ? this.setSession(session, "TOKEN_REFRESHED") : this.session)
      .catch(error => {
        if (generation !== this.generation) return this.session;
        if (error.status === 401 || error.status === 403) return this.setSession(null, "SIGNED_OUT");
        throw error;
      })
      .finally(() => { if (this.refresh === pending) this.refresh = null; });
    this.refresh = pending;
    return pending;
  }
  signInWithPassword(credentials: { email: string; password: string }) {
    const generation = ++this.generation;
    return this.enqueue(async () => {
      if (generation !== this.generation) return this.session;
      const session = await this.request<Session>("/login", credentials);
      if (generation !== this.generation) return null;
      this.notifyTabs("account-changed");
      return this.setSession(session, "SIGNED_IN");
    });
  }
  signOut() {
    const identity = this.session;
    ++this.generation;
    this.setSession(null, "SIGNED_OUT");
    this.notifyTabs("signed-out");
    // Revoke both this tab's session and whichever session owns the shared cookie.
    return this.enqueue(async () => {
      const result = await this.request("/logout", undefined, false, "POST", identity);
      this.notifyTabs("signed-out");
      return result;
    });
  }
  signUp(credentials: { email: string; password: string; locale?: "zh-CN" | "en"; theme?: "light" | "dark" }) {
    return this.request<{ status: "registration_complete" | "verification_required" | "verification_pending" }>("/register", credentials);
  }
  verifyEmail(token: string) { return this.request("/verify-email", {token}); }
  resetPasswordForEmail(email: string) { return this.request("/forgot-password", {email}); }
  async resetPassword(token: string, password: string) {
    ++this.generation;
    await this.enqueue(() => this.request("/reset-password", {token, password}));
    this.notifyTabs("signed-out");
    this.setSession(null, "SIGNED_OUT");
  }
  async changePassword(password: string) {
    const identity = await this.getSession();
    const generation = this.generation;
    await this.enqueue(async () => {
      if (generation !== this.generation) throw new AuthError("请重新登录", "unauthorized", 401);
      await this.request("/change-password", {password}, true, "POST", identity);
      this.notifyTabs("signed-out");
      ++this.generation;
      this.setSession(null, "SIGNED_OUT");
    });
  }
  listMfa() { return this.request<{factors: {id: string; status: string}[]; aal: string; required?: boolean}>("/mfa", undefined, true, "GET"); }
  enrollMfa() { return this.request<{factor_id: string; secret: string; otpauth_uri: string; qr_code: string}>("/mfa/enroll", undefined, true); }
  async verifyMfa(body: {factor_id: string; code: string}) {
    const generation = this.generation;
    const identity = await this.getSession();
    return this.enqueue(async () => {
      if (generation !== this.generation) return null;
      const session = await this.request<Session>("/mfa/verify", body, true, "POST", identity);
      return generation === this.generation ? this.setSession(session, "TOKEN_REFRESHED") : null;
    });
  }

}
export function takeAuthCallback(href: string, history: Pick<History, "replaceState" | "state">) {
  const url = new URL(href);
  const result = { verifyToken: url.searchParams.get("verify_token"), resetToken: url.searchParams.get("reset_token") };
  if (result.verifyToken !== null || result.resetToken !== null) {
    url.searchParams.delete("verify_token");
    url.searchParams.delete("reset_token");
    history.replaceState(history.state, "", url.toString());
  }
  return result;
}
export const auth = new ProductAuth();
